import { useEffect, useState } from "react";
import { Loader2, Send, X, Check, AlertCircle } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

type Outreach = {
  id: number;
  subject: string;
  body: string;
  channel: string;
  status: string;
  adminNote: string | null;
  createdAt: string;
  updatedAt: string;
  banker: { id: number; name: string; role: string } | null;
  investor: { id: number; name: string; email: string } | null;
  task: { id: number; title: string; investorIntent: string | null } | null;
  company: { id: number; name: string } | null;
};

export default function AdminOutreachApprovals() {
  const [rows, setRows] = useState<Outreach[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<Outreach | null>(null);
  const [editedSubject, setEditedSubject] = useState("");
  const [editedBody, setEditedBody] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<"approve" | "reject" | null>(null);
  const [tab, setTab] = useState<"pending_admin_approval" | "rejected" | "sent">("pending_admin_approval");

  const load = async () => {
    setLoading(true);
    const token = localStorage.getItem("admin_token");
    try {
      const r = await fetch(`${BASE}api/admin/outreach-approvals?status=${tab}`, { headers: { "x-admin-token": token || "" } });
      if (r.ok) setRows(await r.json());
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [tab]);

  const open = (o: Outreach) => {
    setActive(o); setEditedSubject(o.subject); setEditedBody(o.body); setNote(""); setErr(null); setMode(null);
  };

  const submitApprove = async () => {
    if (!active) return;
    setBusy(true); setErr(null);
    const token = localStorage.getItem("admin_token");
    try {
      const r = await fetch(`${BASE}api/admin/outreach-approvals/${active.id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-admin-token": token || "" },
        body: JSON.stringify({
          editedSubject: editedSubject !== active.subject ? editedSubject : undefined,
          editedBody: editedBody !== active.body ? editedBody : undefined,
          note: note.trim() || undefined,
        }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); setErr(e.error || "Approve failed"); }
      else { setActive(null); await load(); }
    } catch { setErr("Network error"); }
    setBusy(false);
  };

  const submitReject = async () => {
    if (!active || !note.trim()) { setErr("Add a note explaining what to fix."); return; }
    setBusy(true); setErr(null);
    const token = localStorage.getItem("admin_token");
    try {
      const r = await fetch(`${BASE}api/admin/outreach-approvals/${active.id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-admin-token": token || "" },
        body: JSON.stringify({ note: note.trim() }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); setErr(e.error || "Reject failed"); }
      else { setActive(null); await load(); }
    } catch { setErr("Network error"); }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-serif font-bold">Advisor outreach approvals</h1>
        <p className="text-muted-foreground text-sm mt-1">Drafts that advisors want to send to investors. Approve as-is, edit before sending, or send back with a note.</p>
      </div>

      <div className="flex gap-1 border-b border-border">
        {([
          ["pending_admin_approval", "Pending"],
          ["rejected", "Sent back"],
          ["sent", "Recently sent"],
        ] as const).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground text-sm">Nothing in this queue.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((o) => (
            <button key={o.id} onClick={() => open(o)}
              className="w-full text-left bg-card hover:bg-muted/50 border border-border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded bg-amber-500/15 text-amber-600 border border-amber-500/30">
                  {o.status === "pending_admin_approval" ? "Pending" : o.status === "rejected" ? "Sent back" : "Sent"}
                </span>
                <span className="text-foreground font-medium flex-1 truncate">{o.subject}</span>
                <span className="text-muted-foreground text-[11px] font-mono">{new Date(o.updatedAt).toLocaleString()}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                <span className="font-medium">{o.banker?.name || "Unknown advisor"}</span> → <span className="font-medium">{o.investor?.name || "Unknown investor"}</span>
                {o.task && <span> · re: {o.task.title}</span>}
                {o.company && <span> · {o.company.name}</span>}
              </div>
              <div className="text-sm text-muted-foreground mt-2 line-clamp-2">{o.body}</div>
            </button>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setActive(null)}>
          <div className="bg-background border border-border rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Review outreach</p>
                <p className="text-lg font-semibold mt-1">
                  {active.banker?.name} → {active.investor?.name} <span className="text-muted-foreground text-sm font-normal">({active.investor?.email})</span>
                </p>
                {active.task && <p className="text-xs text-muted-foreground mt-1">Re: {active.task.title}</p>}
              </div>
              <button onClick={() => !busy && setActive(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>

            {active.task?.investorIntent && (
              <div className="bg-muted/50 border border-border rounded-lg p-3 mb-4 text-xs">
                <p className="uppercase tracking-wider font-mono text-muted-foreground mb-1">Original investor ask</p>
                <p>{active.task.investorIntent}</p>
              </div>
            )}

            {err && <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-red-600 text-xs mb-3 flex items-center gap-2"><AlertCircle className="w-3.5 h-3.5" /> {err}</div>}

            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Subject</span>
              <input value={editedSubject} onChange={(e) => setEditedSubject(e.target.value)} maxLength={200}
                disabled={active.status !== "pending_admin_approval"}
                className="mt-1 w-full bg-card border border-border rounded-lg px-3 py-2 text-sm" />
            </label>
            <label className="block mt-3">
              <span className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Body</span>
              <textarea value={editedBody} onChange={(e) => setEditedBody(e.target.value)} rows={14} maxLength={8000}
                disabled={active.status !== "pending_admin_approval"}
                className="mt-1 w-full bg-card border border-border rounded-lg px-3 py-2 text-sm resize-none leading-relaxed" />
            </label>
            {active.adminNote && active.status === "rejected" && (
              <div className="bg-muted/50 border border-border rounded-lg p-3 mt-3 text-xs">
                <p className="uppercase tracking-wider font-mono text-muted-foreground mb-1">Last admin note</p>
                <p>{active.adminNote}</p>
              </div>
            )}

            {active.status === "pending_admin_approval" && (
              <>
                <label className="block mt-3">
                  <span className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Note {mode === "reject" ? "(required)" : "(optional)"}</span>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={400}
                    placeholder={mode === "reject" ? "What should the advisor change?" : "Optional context for the advisor"}
                    className="mt-1 w-full bg-card border border-border rounded-lg px-3 py-2 text-sm resize-none" />
                </label>
                <div className="flex gap-2 mt-5">
                  <button onClick={submitApprove} disabled={busy}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2">
                    {busy && mode !== "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {editedSubject !== active.subject || editedBody !== active.body ? "Approve with edits & send" : "Approve & send"}
                  </button>
                  <button onClick={() => { setMode("reject"); submitReject(); }} disabled={busy}
                    className="px-4 py-2.5 border border-red-500/40 text-red-600 hover:bg-red-500/10 rounded-lg text-sm font-medium flex items-center gap-2">
                    {busy && mode === "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 rotate-180" />}
                    Send back
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
